import { useCallback, useEffect, useState } from "react";
import Layout from "layout/Layout";
import useHttp from "hooks/useHttp";
import useAnalyticsFilters from "hooks/useAnalyticsFilters";
import { getGa4Status, syncGa4 } from "services/ga4-analytics.service";
import Ga4StatusPanel from "pages/google-analytics/components/Ga4StatusPanel";
import Ga4KpiCards from "./components/Ga4KpiCards";

export default function Ga4Page() {
  const api = useHttp();
  const { filters } = useAnalyticsFilters("thisMonth");
  const [ga4Status, setGa4Status] = useState(null);
  const [ga4StatusLoading, setGa4StatusLoading] = useState(true);
  const [ga4Syncing, setGa4Syncing] = useState(false);
  const [ga4SyncError, setGa4SyncError] = useState(null);
  const [ga4SyncSuccess, setGa4SyncSuccess] = useState(null);

  const loadGa4Status = useCallback(async () => {
    setGa4StatusLoading(true);
    try {
      setGa4Status(await getGa4Status(api));
    } catch {
      setGa4Status({
        configured: false,
        availability: "GA4_UNAVAILABLE",
        message: "No GA4 credentials configured",
        checkpoint: null,
      });
    } finally {
      setGa4StatusLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadGa4Status();
  }, [loadGa4Status]);

  const handleGa4Sync = useCallback(async () => {
    setGa4Syncing(true);
    setGa4SyncError(null);
    setGa4SyncSuccess(null);
    try {
      const result = await syncGa4(api, { filters });
      setGa4SyncSuccess(result.message || "GA4 sync completed.");
      await loadGa4Status();
    } catch (err) {
      setGa4SyncError(
        err?.response?.data?.message || err?.message || "GA4 sync failed."
      );
    } finally {
      setGa4Syncing(false);
    }
  }, [api, filters, loadGa4Status]);

  const showGa4Overview = ga4Status?.configured && !ga4StatusLoading;

  return (
    <Layout>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">GA4</h1>

      <Ga4StatusPanel
        status={ga4Status}
        isLoading={ga4StatusLoading}
        isSyncing={ga4Syncing}
        syncError={ga4SyncError}
        syncSuccess={ga4SyncSuccess}
        onSync={handleGa4Sync}
      />

      {showGa4Overview && (
        <div className="mt-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
          <Ga4KpiCards api={api} filters={filters} />
        </div>
      )}
    </Layout>
  );
}
